// Sample-data mode. Factory-owned.
//
// `/demo` opens the app with something already in it, so somebody deciding
// whether to install can see what it looks like in use rather than an empty
// state and a form. It is the single cheapest thing that improves a store
// listing click-through, and every app that has one had to build it late.
//
// Two rules, and they are both about not lying to the user:
//
//   * demo data NEVER touches real storage. Everything is in memory and gone on
//     refresh, so a curious tap cannot pollute a real journal.
//   * the app SAYS it is a demo, permanently and visibly, and offers the way
//     out. A sample that looks like your own data is a support ticket.

import { ref, computed } from 'vue';

// `/app/demo` as well as `/demo`. server.js serves anything under /app/ and
// /demo/ as the app, so a link to /app/demo reaches the SPA either way — and
// with the tighter pattern it reached it in NON-demo mode, showing an empty
// app to somebody who followed a "try it" link. Silent, because nothing errors:
// you get the real app with no data in it.
export const isDemo = ref(
  typeof location !== 'undefined'
  && /^\/(app\/)?demo(\/|$)/.test(location.pathname),
);

/**
 * What QA asked for, if anything.
 *
 * The fixture contract writes `appfactory:qa:seed` before any app code runs —
 * `empty` or `populated` — and nothing in the chassis read it. So a screen that
 * declared an empty state got a screenshot of the populated app, filed under
 * the name `empty`, and the report said the empty state had been audited.
 *
 * That is the same shape of bug as the onboarding overlay covering every
 * screenshot: not a wrong answer, a confident answer to a question nobody
 * asked. Empty states are exactly where apps look worst and get looked at
 * least, which is why QA asks for them.
 *
 * Read once, at module load, because a fixture cannot change mid-run.
 */
const qaSeed = (() => {
  try {
    return localStorage.getItem('appfactory:qa:seed');
  } catch {
    return null;   // private mode; behave as though nothing was asked for
  }
})();

/** Seed rows for a demo run, or an empty array in the real app. */
export function demoData(rows) {
  return computed(() => {
    if (!isDemo.value) return [];
    if (qaSeed === 'empty') return [];
    return rows;
  });
}
